/**
 * @file SymbolEffectController.ts
 * @role Applies win-presentation effects to SymbolViews and, crucially, clears them all
 *       back to a pristine state afterwards (Invariant 12/18). Dim makes a losing symbol
 *       recede without fading its backplate; activateWithStyle un-dims a winner, runs a finite
 *       configured motion (GSAP, polish only, ADR 0003), and applies a preset-driven glow.
 *       shake/colorPulse/glowBackground/shineSweep/sparkleOverlay are additional per-symbol FX library methods, each
 *       mapped to an existing destroy-safe SymbolView socket. Every touched view is tracked
 *       (with its live tweens/timelines, if any) so clear() can kill them all, retire any
 *       particles it owns, clear the glow, and reset() the view; nothing leaks across spins.
 * @owns The map of touched views -> their live tweens/timelines.
 * @reads SymbolEffectPresets + a dim alpha passed in; validated win/symbol-fx tuning.
 * @neverDoes Decide which symbols win (the mapper/controller pass them in). Drive reel
 *            motion. Persist effects past clear(). Decide WHEN an effect fires.
 * @invariants 8 (render-only effects), 10 (config validated), 12/18 (clear() fully resets
 *             every touched symbol, including retiring any particles it owns first).
 * @see src/game/reels/SymbolView.ts, src/game/wins/WinPresentationController.ts,
 *      src/core/postfx/PostFxService.ts, src/core/particles/ParticleService.ts
 */
import { gsap } from 'gsap';
import { Container, Graphics } from 'pixi.js';
import { z } from 'zod';
import type { PostFxService } from '@core/postfx/PostFxService';
import type { ParticleService } from '@core/particles/ParticleService';
import { GameError } from '@core/errors/GameError';
import type { SymbolView } from './SymbolView';
import type { SymbolEffectPreset } from './SymbolEffectPreset';
import type { SymbolAnimationStyle } from '@game/wins/WinConfig';

interface TouchedEffects {
  activation?: gsap.core.Timeline;
  sustainPulse?: gsap.core.Tween;
  colorPulse?: gsap.core.Tween;
  shine?: { root: Container; timeline: gsap.core.Timeline };
  settle?: gsap.core.Timeline;
}

interface HaloEffect {
  graphic: Graphics;
  timeline: gsap.core.Animation;
}

export interface SymbolActivationOptions {
  readonly durationMs: number;
  readonly peakScale: number;
  readonly compressScale: number;
  readonly rotationDeg?: number;
  readonly useFilterGlow?: boolean;
}

// --- symbol-fx.config.json schema (tier/symbol tuning) ---

const ShakeConfigSchema = z.object({
  amplitude: z.number().nonnegative().optional(),
});

const ColorPulseConfigSchema = z.object({
  colorA: z.number().int().min(0).max(0xffffff),
  colorB: z.number().int().min(0).max(0xffffff),
  speedMs: z.number().positive(),
});

const GlowBackgroundConfigSchema = z.object({
  color: z.number().int().min(0).max(0xffffff),
  radiusRatio: z.number().positive(),
  alpha: z.number().min(0).max(1),
  durationMs: z.number().positive().default(500),
});

const SparkleOverlayConfigSchema = z.object({
  preset: z.string().min(1),
});

const ShineSweepConfigSchema = z.object({
  durationMs: z.number().positive(),
});

const SymbolFxEntrySchema = z.object({
  shake: ShakeConfigSchema.optional(),
  colorPulse: ColorPulseConfigSchema.optional(),
  glowBackground: GlowBackgroundConfigSchema.optional(),
  shineSweep: ShineSweepConfigSchema.optional(),
  sparkleOverlay: SparkleOverlayConfigSchema.optional(),
});

export const SymbolFxConfigSchema = z.object({
  tiers: z
    .object({
      small: SymbolFxEntrySchema,
      medium: SymbolFxEntrySchema,
      big: SymbolFxEntrySchema,
      jackpot: SymbolFxEntrySchema,
    })
    .strict(),
  symbolOverrides: z.record(z.string(), SymbolFxEntrySchema).default({}),
});

export type SymbolFxEntry = z.infer<typeof SymbolFxEntrySchema>;
export type SymbolFxConfig = z.infer<typeof SymbolFxConfigSchema>;
export type ShakeFxConfig = z.infer<typeof ShakeConfigSchema>;
export type ColorPulseFxConfig = z.infer<typeof ColorPulseConfigSchema>;
export type GlowBackgroundFxConfig = z.infer<typeof GlowBackgroundConfigSchema>;
export type ShineSweepFxConfig = z.infer<typeof ShineSweepConfigSchema>;
export type SparkleOverlayFxConfig = z.infer<typeof SparkleOverlayConfigSchema>;

export function validateSymbolFxConfig(raw: unknown): SymbolFxConfig {
  const parsed = SymbolFxConfigSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError('CONFIG_LOAD_FAILED', `Invalid symbol-fx config: ${parsed.error.message}`, {
      cause: parsed.error,
    });
  }
  return parsed.data;
}

function lerpChannel(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

/** Perceptually-correct per-channel RGB lerp between two 24-bit hex colors. */
function lerpColor(colorA: number, colorB: number, t: number): number {
  const ar = (colorA >> 16) & 0xff;
  const ag = (colorA >> 8) & 0xff;
  const ab = colorA & 0xff;
  const br = (colorB >> 16) & 0xff;
  const bg = (colorB >> 8) & 0xff;
  const bb = colorB & 0xff;
  const r = lerpChannel(ar, br, t);
  const g = lerpChannel(ag, bg, t);
  const b = lerpChannel(ab, bb, t);
  return (r << 16) | (g << 8) | b;
}

export class SymbolEffectController {
  // Key = a touched view; value = its live tweens/timelines (undefined slots for effects that
  // aren't active on this view). `touched.has(view)` is set even for dim-only views.
  private readonly touched = new Map<SymbolView, TouchedEffects>();
  private readonly halos = new Map<SymbolView, HaloEffect>();

  constructor(
    private readonly postFx: PostFxService,
    private readonly particles: ParticleService,
  ) {}

  /** Push a symbol back so winners stand out. */
  dim(view: SymbolView, alpha: number): void {
    if (!this.touched.has(view)) this.touched.set(view, {});
    view.setDimAlpha(alpha);
  }

  /** Bring a winning symbol forward with a finite slot-style punch and preset glow. */
  activate(
    view: SymbolView,
    preset: SymbolEffectPreset,
    options: SymbolActivationOptions,
  ): gsap.core.Timeline {
    return this.activateWithStyle(view, preset, options, 'punch');
  }

  /** Runs one of the finite, config-selected slot symbol animations. */
  activateWithStyle(
    view: SymbolView,
    preset: SymbolEffectPreset,
    options: SymbolActivationOptions,
    style: SymbolAnimationStyle,
  ): gsap.core.Timeline {
    view.setDimAlpha(1);
    view.setHighlighted(true);
    const entry = this.touched.get(view) ?? {};
    entry.activation?.kill();
    entry.sustainPulse?.kill();
    view.artRoot.position.set(0);
    view.artRoot.scale.set(1);
    view.artRoot.rotation = 0;
    const duration = options.durationMs / 1000;
    const rotation = ((options.rotationDeg ?? 0) * Math.PI) / 180;
    const timeline = gsap.timeline();
    if (style === 'squash') {
      timeline
        .to(view.artRoot.scale, {
          x: options.peakScale,
          y: options.compressScale,
          duration: duration * 0.36,
          ease: 'power2.out',
        })
        .to(view.artRoot.scale, {
          x: options.compressScale,
          y: Math.min(options.peakScale, 1.08),
          duration: duration * 0.28,
          ease: 'power2.inOut',
        })
        .to(view.artRoot.scale, { x: 1, y: 1, duration: duration * 0.36, ease: 'back.out(2)' });
    } else if (style === 'bounce') {
      const lift = -Math.max(6, view.cellSize.height * 0.1);
      timeline
        .to(view.artRoot, { y: lift, duration: duration * 0.34, ease: 'power2.out' })
        .to(view.artRoot, { y: 3, duration: duration * 0.31, ease: 'power2.in' })
        .to(view.artRoot, { y: 0, duration: duration * 0.35, ease: 'bounce.out' });
      timeline.to(
        view.artRoot.scale,
        { x: options.peakScale, y: options.peakScale, duration: duration * 0.34, ease: 'sine.out' },
        0,
      );
      timeline.to(
        view.artRoot.scale,
        { x: 1, y: 1, duration: duration * 0.4, ease: 'back.out(1.8)' },
        duration * 0.6,
      );
    } else if (style === 'wiggle') {
      const wiggle = rotation || (5 * Math.PI) / 180;
      timeline
        .to(view.artRoot, { rotation: wiggle, duration: duration * 0.2, ease: 'sine.out' })
        .to(view.artRoot, { rotation: -wiggle, duration: duration * 0.26, ease: 'sine.inOut' })
        .to(view.artRoot, { rotation: wiggle * 0.5, duration: duration * 0.24, ease: 'sine.inOut' })
        .to(view.artRoot, { rotation: 0, duration: duration * 0.3, ease: 'sine.out' });
      timeline.to(
        view.artRoot.scale,
        {
          x: options.peakScale,
          y: options.peakScale,
          duration: duration * 0.35,
          yoyo: true,
          repeat: 1,
        },
        0,
      );
    } else if (style === 'elastic') {
      view.artRoot.scale.set(options.compressScale);
      timeline.to(view.artRoot.scale, {
        x: 1,
        y: 1,
        duration,
        ease: 'elastic.out(1, 0.3)',
      });
    } else {
      timeline.to(view.artRoot.scale, {
        x: options.peakScale,
        y: options.peakScale,
        duration: duration * 0.45,
        ease: 'back.out(2.2)',
      });
      if (rotation !== 0) {
        timeline.to(view.artRoot, { rotation, duration: duration * 0.22, ease: 'sine.out' }, 0);
        timeline.to(
          view.artRoot,
          { rotation: -rotation * 0.5, duration: duration * 0.23, ease: 'sine.inOut' },
          duration * 0.22,
        );
      }
      timeline.to(view.artRoot.scale, {
        x: options.compressScale,
        y: options.compressScale,
        duration: duration * 0.22,
        ease: 'power2.inOut',
      });
      timeline.to(view.artRoot.scale, {
        x: 1,
        y: 1,
        duration: duration * 0.33,
        ease: 'back.out(1.4)',
      });
      if (rotation !== 0) {
        timeline.to(
          view.artRoot,
          { rotation: 0, duration: duration * 0.33, ease: 'sine.out' },
          '<',
        );
      }
    }
    entry.activation = timeline;
    this.touched.set(view, entry);
    if (options.useFilterGlow !== false) this.postFx.glow(view.artRoot, preset);
    return timeline;
  }

  /** Compatibility entry point for isolated previews and older callers. */
  highlight(view: SymbolView, preset: SymbolEffectPreset): void {
    this.activate(view, preset, {
      durationMs: Math.max(360, preset.pulseMs),
      peakScale: Math.min(preset.pulseScale, 1.18),
      compressScale: 0.98,
    });
  }

  /** Low-amplitude loop used only while large-win amounts are counting. */
  sustain(view: SymbolView, scale = 1.035, durationMs = 700): void {
    const entry = this.touched.get(view) ?? {};
    entry.activation?.kill();
    view.artRoot.position.set(0);
    view.artRoot.scale.set(1);
    view.artRoot.rotation = 0;
    entry.sustainPulse?.kill();
    entry.sustainPulse = gsap.to(view.artRoot.scale, {
      x: scale,
      y: scale,
      duration: durationMs / 2000,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
    this.touched.set(view, entry);
  }

  /**
   * Shake the symbol's art in place. Reuses PostFxService.screenShake() (generic despite the
   * name — it only ever tweens the position of whatever container is passed in, with its own
   * retrigger-safe origin tracking), applied to `view.artRoot` instead of the board. Callers
   * must not rely on this tween outliving clear(): clear() kills it before view.reset() resets
   * artRoot's position, so the still-running shake can't silently re-jitter after teardown.
   * `amplitude`, when supplied, overrides PostFxService's own fx.config.json default — this is
   * the plumbing that makes per-tier `symbol-fx.config.json` shake.amplitude values real; the
   * caller that decides which tier's amplitude to pass is Phase F, not this controller.
   */
  shake(view: SymbolView, amplitude?: number): gsap.core.Timeline {
    const entry = this.touched.get(view) ?? {};
    const timeline = this.postFx.screenShake(
      view.artRoot,
      amplitude === undefined ? {} : { amplitude },
    );
    this.touched.set(view, entry);
    return timeline;
  }

  /**
   * Tween `view.artMainLayer.tint` between two colors, yoyo-repeating forever until cleared.
   * A naive GSAP tween of the raw hex int would NOT interpolate perceptually (it would just
   * move linearly through the packed 24-bit integer, not per-channel), so this tweens a proxy
   * progress value and computes the interpolated color per-channel in onUpdate.
   */
  colorPulse(view: SymbolView, colorA: number, colorB: number, speedMs: number): gsap.core.Tween {
    const entry = this.touched.get(view) ?? {};
    entry.colorPulse?.kill();
    const proxy = { t: 0 };
    const tween = gsap.to(proxy, {
      t: 1,
      duration: speedMs / 1000,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
      onUpdate: () => {
        view.artMainLayer.tint = lerpColor(colorA, colorB, proxy.t);
      },
    });
    entry.colorPulse = tween;
    this.touched.set(view, entry);
    return tween;
  }

  /**
   * Draws a halo in board space so it composites above every pooled reel cell, independent of
   * cell sibling order. `view` remains the stable ownership/retrigger key.
   */
  glowBackgroundAt(
    view: SymbolView,
    targetLayer: Container,
    point: { x: number; y: number },
    color: number,
    radiusRatio: number,
    alpha: number,
    durationMs = 500,
  ): gsap.core.Timeline {
    const previous = this.halos.get(view);
    previous?.timeline.kill();
    destroyGraphics(previous?.graphic);
    const { width, height } = view.cellSize;
    // `radiusRatio` describes the aura diameter relative to the cell. The previous
    // implementation treated it as a radius, producing a 1.6-cell-wide disc at 0.8.
    const radius = Math.min(width, height) * Math.min(radiusRatio, 1.2) * 0.5;
    const glow = new Graphics();
    glow.circle(0, 0, radius).fill({ color, alpha: alpha * 0.18 });
    const rings = 10;
    for (let i = rings; i >= 1; i -= 1) {
      const ringRadius = (radius * i) / rings;
      const inward = 1 - (i - 1) / rings;
      // Enough accumulated opacity to read behind opaque fruit art, while the outer ring
      // remains soft and contained inside the cell.
      const ringAlpha = alpha * (0.04 + inward * 0.025);
      glow.circle(0, 0, ringRadius).fill({ color, alpha: ringAlpha });
    }
    glow.blendMode = 'add';
    glow.alpha = 0;
    glow.scale.set(0.72);
    glow.position.set(point.x, point.y);
    targetLayer.addChild(glow);
    const seconds = durationMs / 1000;
    const timeline = gsap.timeline({
      onComplete: () => {
        if (this.halos.get(view)?.graphic === glow) this.halos.delete(view);
        destroyGraphics(glow);
      },
    });
    timeline.to(glow, { alpha: 1, duration: seconds * 0.22, ease: 'power2.out' }, 0);
    timeline.to(glow.scale, { x: 1.08, y: 1.08, duration: seconds, ease: 'sine.out' }, 0);
    timeline.to(glow, { alpha: 0, duration: seconds * 0.42, ease: 'power2.in' }, seconds * 0.58);
    this.halos.set(view, { graphic: glow, timeline });
    if (!this.touched.has(view)) this.touched.set(view, {});
    return timeline;
  }

  /** A finite, clipped diagonal glint that can be layered with the halo and motion styles. */
  shineSweep(view: SymbolView, durationMs = 600): gsap.core.Timeline {
    const entry = this.touched.get(view) ?? {};
    entry.shine?.timeline.kill();
    destroyContainer(entry.shine?.root);
    const { width, height } = view.cellSize;
    const root = new Container();
    const sweep = new Container();
    const alphaMask = view.createPrimaryArtAlphaMask();
    const strip = new Graphics()
      .rect(-width * 0.08, -height * 0.7, width * 0.16, height * 1.4)
      .fill({ color: 0xffffff, alpha: 0.72 });
    strip.rotation = -Math.PI / 7;
    strip.blendMode = 'add';
    strip.x = -width * 0.65;
    sweep.addChild(strip);
    root.addChild(sweep);
    if (alphaMask) {
      root.addChild(alphaMask);
      sweep.setMask({ mask: alphaMask, channel: 'alpha' });
    } else {
      const fallbackMask = new Graphics()
        .roundRect(-width * 0.42, -height * 0.42, width * 0.84, height * 0.84, 12)
        .fill(0xffffff);
      root.addChild(fallbackMask);
      sweep.mask = fallbackMask;
    }
    view.localOverFxLayer.addChild(root);
    const timeline = gsap.timeline({
      onComplete: () => {
        if (entry.shine?.root === root) delete entry.shine;
        destroyContainer(root);
      },
    });
    timeline.to(strip, {
      x: width * 0.65,
      duration: durationMs / 1000,
      ease: 'power1.inOut',
    });
    entry.shine = { root, timeline };
    this.touched.set(view, entry);
    return timeline;
  }

  /**
   * Spawn a pooled particle burst into the destroy-safe `localOverFxLayer` socket, centered on
   * the cell (local coordinates, since the layer is a child of SymbolView which is itself
   * positioned at the cell). Requires clear() to retire these particles through
   * ParticleService.retireIn() BEFORE view.reset() destroys the layer's children, since these
   * sprites are still tracked in ParticleService's live-particle list and TexturePool.
   */
  sparkleOverlay(view: SymbolView, presetName: string): void {
    if (!this.touched.has(view)) this.touched.set(view, {});
    this.particles.emit(presetName, 0, 0, view.localOverFxLayer);
  }

  /** Ease every touched symbol back toward its canonical at-rest presentation before clear(). */
  settleAll(durationMs: number): gsap.core.Animation[] {
    const seconds = Math.max(1, durationMs) / 1000;
    const animations: gsap.core.Animation[] = [];
    for (const [view, entry] of this.touched) {
      entry.activation?.kill();
      entry.sustainPulse?.kill();
      entry.colorPulse?.kill();
      this.postFx.clearScreenShake(view.artRoot);
      const tintState = { progress: 0 };
      const startTint = view.artMainLayer.tint;
      entry.settle?.kill();
      entry.settle = gsap
        .timeline({ onComplete: () => view.setHighlighted(false) })
        .to(
          view.artRoot,
          { x: 0, y: 0, rotation: 0, alpha: 1, duration: seconds, ease: 'power2.inOut' },
          0,
        )
        .to(view.artRoot.scale, { x: 1, y: 1, duration: seconds, ease: 'power2.inOut' }, 0)
        .to(
          [view.localUnderFxLayer, view.localOverFxLayer],
          { alpha: 1, duration: seconds, ease: 'power2.inOut' },
          0,
        )
        .to(
          tintState,
          {
            progress: 1,
            duration: seconds,
            ease: 'power2.inOut',
            onUpdate: () => {
              view.artMainLayer.tint = lerpColor(startTint, 0xffffff, tintState.progress);
            },
          },
          0,
        );
      animations.push(entry.settle);
      const glowFade = this.postFx.fadeGlow(view.artRoot, durationMs);
      if (glowFade) animations.push(glowFade);
    }

    for (const [view, halo] of this.halos) {
      halo.timeline.kill();
      const fade = gsap.to(halo.graphic, {
        alpha: 0,
        duration: seconds,
        ease: 'power2.in',
        onComplete: () => {
          if (this.halos.get(view)?.graphic === halo.graphic) this.halos.delete(view);
          destroyGraphics(halo.graphic);
        },
      });
      halo.timeline = fade;
      animations.push(fade);
    }
    return animations;
  }

  /** Kill every live tween/timeline, retire particles, clear every glow, and reset every
   *  touched symbol (Invariant 12/18). */
  clear(): void {
    for (const halo of this.halos.values()) {
      halo.timeline.kill();
      destroyGraphics(halo.graphic);
    }
    this.halos.clear();
    for (const [view, entry] of this.touched) {
      entry.activation?.kill();
      entry.sustainPulse?.kill();
      this.postFx.clearScreenShake(view.artRoot);
      entry.colorPulse?.kill();
      entry.settle?.kill();
      entry.shine?.timeline.kill();
      destroyContainer(entry.shine?.root);
      // Particles spawned into localOverFxLayer must be retired through ParticleService's own
      // release path BEFORE view.reset() destroys that layer's children, or update() will
      // mutate a destroyed Sprite next frame and the TexturePool bookkeeping will be wrong.
      this.particles.retireIn(view.localOverFxLayer);
      this.postFx.clearGlow(view.artRoot);
      view.reset();
    }
    this.touched.clear();
  }
}

function destroyGraphics(graphic: Graphics | undefined): void {
  if (!graphic || graphic.destroyed) return;
  graphic.removeFromParent();
  graphic.destroy();
}

function destroyContainer(container: Container | undefined): void {
  if (!container || container.destroyed) return;
  container.removeFromParent();
  container.destroy({ children: true });
}
